/**
 * Localized country name for the markets Ahead serves, plus a helper that
 * renders a clinic address with its country correctly labelled.
 *
 * Every clinic shown on a location page belongs to the page's own market
 * (`getLocations` filters clinics to the URL country), so the country to show
 * is the market country — NOT a hardcoded "Switzerland". Before this helper,
 * the clinic-detail and city-overview surfaces appended a hardcoded
 * `cp_switzerland` ("Schweiz"), so the Munich (DE) clinic read
 * "…München, Germany, Schweiz".
 *
 * Names live in code (not messages) to match `lib/llms-txt.ts`, which already
 * maps ISO → localized country name per locale; these are a closed, stable set
 * of proper nouns and don't flow through the translation-review pipeline.
 */

// URL locale (en/de/fr/nl) → ISO country code (ch/de/nl/at) → country name.
const COUNTRY_NAMES: Record<string, Record<string, string>> = {
  en: { ch: "Switzerland", de: "Germany", nl: "Netherlands", at: "Austria" },
  de: { ch: "Schweiz", de: "Deutschland", nl: "Niederlande", at: "Österreich" },
  fr: { ch: "Suisse", de: "Allemagne", nl: "Pays-Bas", at: "Autriche" },
  nl: { ch: "Zwitserland", de: "Duitsland", nl: "Nederland", at: "Oostenrijk" },
}

// Every country name in any language — used to detect and strip a country that
// a CMS address already carries, so appending the market country never doubles
// it (a handful of clinic addresses end in ", Germany" / ", Switzerland").
const ALL_COUNTRY_NAMES = new Set(
  Object.values(COUNTRY_NAMES).flatMap((byCountry) =>
    Object.values(byCountry).map((name) => name.toLowerCase())
  )
)

/** Localized name of a market's country, e.g. ("de","de") → "Deutschland". */
export function countryDisplayName(country: string, locale: string): string {
  const byCountry = COUNTRY_NAMES[locale] ?? COUNTRY_NAMES.en
  return (
    byCountry[country.toLowerCase()] ??
    COUNTRY_NAMES.en[country.toLowerCase()] ??
    country.toUpperCase()
  )
}

/**
 * A clinic's postal address with its market country appended in the current
 * language. Strips any country segment the address already carries first, so
 * the result is one consistent, correctly-localized country per clinic.
 */
export function addressWithCountry(
  address: string,
  country: string,
  locale: string
): string {
  const segments = address.split(",").map((s) => s.trim())
  if (
    segments.length > 1 &&
    ALL_COUNTRY_NAMES.has(segments[segments.length - 1]!.toLowerCase())
  ) {
    segments.pop()
  }
  return `${segments.join(", ")}, ${countryDisplayName(country, locale)}`
}
